/**
 * Centralized deduplication layer for ad/listing arrays.
 *
 * Every render path that displays ads MUST pass through dedupeAdsById()
 * before reaching the UI. This prevents duplicates regardless of how
 * many data sources are merged (search results, feeds, sections, etc.).
 */

/**
 * Deduplicate an array of ads by their unique listing ID.
 * Preserves the order of first occurrence.
 *
 * @param {Array} ads - Array of ad objects
 * @param {string} [idKey] - Optional override for the ID field name
 * @returns {Array} Deduplicated array (first occurrence wins)
 */
export function dedupeAdsById(ads, idKey) {
  if (!Array.isArray(ads) || ads.length === 0) return [];
  const seen = new Set();
  return ads.filter((ad) => {
    if (!ad) return false;
    const id = idKey ? ad[idKey] : (ad.ad_id || ad.id || ad._id);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * Merge multiple ad arrays into a single deduplicated list.
 * Earlier arrays have priority (their items appear first).
 *
 * @param  {...Array} arrays - Any number of ad arrays to merge
 * @returns {Array} Merged, deduplicated array
 */
export function mergeAndDedupeAds(...arrays) {
  const combined = arrays.flat().filter(Boolean);
  return dedupeAdsById(combined);
}
